import { useEffect, useRef } from "react";

const DEPTH_KEY = "overlayDepth";
const RELEASED_KEY = "overlayReleased";

const readState = (): Record<string, unknown> => {
  const state: unknown = window.history.state;
  return state && typeof state === "object"
    ? (state as Record<string, unknown>)
    : {};
};

const readNumber = (key: string): number => {
  const value = readState()[key];
  return typeof value === "number" ? value : 0;
};

const currentDepth = (): number => readNumber(DEPTH_KEY);

type NavigationLike = {
  currentEntry?: { index: number } | null;
  canGoForward?: boolean;
};

const readNavigation = (): NavigationLike | undefined =>
  (window as Window & { navigation?: NavigationLike }).navigation;

/**
 * A position, with the scale it was read on: two readings compare only on the
 * same scale. Both scales never coexist in one document in practice — an
 * active page's entry always has an index — but nothing would stop one
 * reading from each, and the comparison between them would be arbitrary.
 */
type HistoryPosition = {
  readonly value: number;
  readonly scale: "navigation" | "router";
};

/**
 * Where the current entry sits in history, increasing from the oldest entry to
 * the newest — or `NaN` when unknown, which every comparison reads as a back.
 *
 * The Navigation API knows it exactly. Without it, React Router's index for the
 * page, then the overlay depth on that page — a released entry keeps its depth
 * and sits just above it, below any overlay stacked on it. Strictly increasing,
 * or back and forward could bounce between two entries without end; an entry
 * that lost the router's index (a `replaceState(null)`) has no position.
 */
const currentPosition = (): HistoryPosition => {
  const index = readNavigation()?.currentEntry?.index;
  if (typeof index === "number" && index >= 0) {
    return { value: index, scale: "navigation" };
  }
  const state = readState();
  if (typeof state.idx !== "number" || !Number.isFinite(state.idx)) {
    return { value: Number.NaN, scale: "router" };
  }
  return {
    value:
      state.idx * 1000 +
      currentDepth() * 2 +
      (state[RELEASED_KEY] === true ? 1 : 0),
    scale: "router",
  };
};

/** Whether `to` is further forward than `from`; across scales, never. */
const isAhead = (to: HistoryPosition, from: HistoryPosition): boolean =>
  to.scale === from.scale && to.value > from.value;

/**
 * The entry an overlay owns: its page's router key — unique per entry, where
 * the index can repeat or be lost — and its depth on that page.
 */
const currentEntryKey = (): string =>
  `${String(readState().key ?? readNumber("idx"))}:${currentDepth()}`;

/** Entries owned by an overlay open right now, across every caller. */
const liveEntries = new Set<string>();
/**
 * The position of the entry the user is on, to tell a back from a forward when
 * they leave it: recorded on each popstate, each overlay push, and — since the
 * router's own pushes fire no event — by `recordHistoryPosition` on every
 * location change.
 */
let lastPosition: HistoryPosition = { value: 0, scale: "router" };
/** Popstates seen, to tell whether a forward the app asked for landed. */
let popstateCount = 0;

/**
 * Records the current entry as where the user is. Called on every location
 * change by the mobile layout: a stale entry reached by a back from a page the
 * router pushed is then read as a back, not a forward that bounces the user
 * back to that page.
 */
export function recordHistoryPosition(): void {
  lastPosition = currentPosition();
}
/**
 * Set while a step back the app asked for has not landed. One at a time: a
 * second `back()` queued behind the first would step out of the page. Every
 * entry the app steps back off was pushed on top of another, so the step
 * always lands and clears it. A forward may have nowhere to go and never land:
 * it is not tracked, and where it lands reads as forward from the position.
 */
let isSteppingBack = false;

/** How long a forward the app asked for has to land before it is given up. */
const FORWARD_LANDING_MS = 100;

/**
 * `setTimeout`, dropped once the window is gone: a test environment tears its
 * window down when its last test ends, possibly with a step still queued. A
 * browser's window outlives every task, so nothing changes there.
 */
const afterTask = (step: () => void, delayMs = 0): void => {
  setTimeout(() => {
    if (typeof window === "undefined") return;
    step();
  }, delayMs);
};

/**
 * Steps forward over a stale entry — or, with nothing ahead of it, back: the
 * user pressed forward onto an entry a tap-closed overlay released, the last
 * one, and stopping there would leave them on an entry that shows nothing and
 * swallows the next back. The page below is where they were.
 */
const stepForwardOverStale = (): void => {
  if (readNavigation()?.canGoForward === false) {
    traverse("back");
    return;
  }
  const before = popstateCount;
  window.history.forward();
  afterTask(() => {
    if (popstateCount !== before || !isStaleEntry()) return;
    traverse("back");
  }, FORWARD_LANDING_MS);
};

const traverse = (direction: "back" | "forward"): void => {
  if (direction === "forward") {
    stepForwardOverStale();
    return;
  }
  if (isSteppingBack) return;
  isSteppingBack = true;
  window.history.back();
};

/**
 * Marks the current entry released and steps back off it on the next task.
 * Only the top entry is ever released: overlays that close together — a sheet
 * and the FAB menu it came from — leave the lower entries to the stale-entry
 * skip, which takes them one traversal at a time.
 */
const releaseEntry = (): void => {
  window.history.replaceState({ ...readState(), [RELEASED_KEY]: true }, "");
  afterTask(() => {
    // Something took the history over meanwhile — a navigation, an overlay
    // opening on top: the released entry stays, skipped when reached.
    if (readState()[RELEASED_KEY] !== true) return;
    traverse("back");
  });
};

/**
 * An overlay's entry outlives it when the app navigated while it was open, or
 * when it was released under an overlay that opened as it closed: it then sits
 * in history on the same URL, and landing on it would show nothing change. Step
 * over it in the direction the user was going. An entry is stale when it is
 * released, or when it carries a depth no open overlay owns.
 */
const isStaleEntry = (): boolean =>
  readState()[RELEASED_KEY] === true ||
  (currentDepth() > 0 && !liveEntries.has(currentEntryKey()));

const skipStaleEntry = (): void => {
  popstateCount += 1;
  const position = currentPosition();
  // The app's own step back is a back wherever it lands; anything else is
  // read from the position.
  const isForward = !isSteppingBack && isAhead(position, lastPosition);
  isSteppingBack = false;
  lastPosition = position;
  afterTask(() => {
    if (isStaleEntry()) traverse(isForward ? "forward" : "back");
  });
};

/**
 * Loaded on an overlay's entry — reloaded, or the app updated, while a sheet
 * was open: nothing is open now, so the entry is made the page's own again.
 * Rewritten, not stepped off: the entry below belongs to the document from
 * before the reload, and stepping onto it would reload the page once more.
 * Left as it was, the next overlay to close by tap would step onto it, find it
 * stale and step again — into that old document.
 *
 * Known limit: the page's own entry is still below, on the same URL, with the
 * same router index and key — the overlay's entry was a copy of it. Nothing
 * tells the two apart, and a traversal onto it is not the app's to intercept:
 * the first back after such a reload lands on the same screen, and only the
 * second leaves it. Pinned by `useCloseOnHistoryBack.load.test.ts`.
 */
export function adoptLoadedEntry(): void {
  if (currentDepth() === 0 && readState()[RELEASED_KEY] !== true) return;
  window.history.replaceState(
    { ...readState(), [DEPTH_KEY]: 0, [RELEASED_KEY]: false },
    ""
  );
}

if (typeof window !== "undefined") {
  // From load, not from the first overlay: after a reload, back can land on an
  // entry a previous session's overlay left.
  adoptLoadedEntry();
  lastPosition = currentPosition();
  window.addEventListener("popstate", skipStaleEntry);
}

/**
 * Lets the phone's back gesture close an overlay instead of leaving the page,
 * as it does in a native app.
 *
 * While `open`, the overlay owns one history entry — a copy of the current one,
 * same URL and same router index, tagged with its depth. Going back off that
 * entry closes it; overlays stacked on top of each other close one per back.
 * React Router reads every step on and off these entries as a POP to the same
 * location: nothing navigates, but location consumers re-render, so depend on
 * `pathname` rather than on the location object.
 *
 * Closed any other way, it marks its entry released at once and steps back off
 * it on the next task — unless something took the history over in between: a
 * navigation, or another overlay opening as this one closes, whose entry then
 * stacks on the released one instead of being popped by that step back. Those
 * entries are skipped when back or forward lands on them (`skipStaleEntry`).
 *
 * Not covered: a router `replace` while an overlay is open overwrites its entry,
 * which then reads as the page itself and costs one extra back; and a reload
 * while one is open (`adoptLoadedEntry`) costs one back too.
 */
export function useCloseOnHistoryBack(
  open: boolean,
  onClose: () => void
): void {
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;

    // An app back still in flight was overtaken by this overlay: whatever it
    // lands on is read from the position again.
    isSteppingBack = false;
    const depth = currentDepth() + 1;
    window.history.pushState(
      { ...readState(), [DEPTH_KEY]: depth, [RELEASED_KEY]: false },
      ""
    );
    const entryKey = currentEntryKey();
    liveEntries.add(entryKey);
    lastPosition = currentPosition();

    let closedByBack = false;
    const onPopState = () => {
      if (currentDepth() >= depth) return;
      closedByBack = true;
      onCloseRef.current();
    };
    window.addEventListener("popstate", onPopState);

    return () => {
      liveEntries.delete(entryKey);
      window.removeEventListener("popstate", onPopState);
      if (closedByBack || currentDepth() !== depth) return;
      releaseEntry();
    };
  }, [open]);
}
